import React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X, Shield, Calendar, Globe } from "lucide-react";
import { cn } from "../../lib/utils";
import type { 
  ApiGatewayKeyCreateDialogProps, 
  ApiGatewayCreateDraft 
} from "../../contracts/api-gateway-management";
import { Input } from "../../internal/primitives/input";
import { Label } from "../../internal/primitives/label";

/**
 * ApiGatewayKeyCreateDialog is a controlled dialog for creating new API gateway keys.
 * It provides a structured form for key metadata, permission-family selection, and access controls.
 */
export function ApiGatewayKeyCreateDialog({
  open,
  title = "Create API Key",
  description = "New keys provide full programmatic access to your tenant resources until they expire or are revoked.",
  helperText,
  draft,
  validation,
  permissionFamilies,
  submitLabel = "Generate Key",
  cancelLabel = "Cancel",
  submitting,
  onOpenChange,
  onDraftChange,
  onSubmit,
  className,
}: ApiGatewayKeyCreateDialogProps) {
  const handleInputChange = (field: keyof ApiGatewayCreateDraft, value: any) => {
    onDraftChange({ ...draft, [field]: value });
  };

  const setFamilyLevel = (
    readPermission: string,
    managePermission: string,
    nextLevel: "none" | "view" | "manage"
  ) => {
    const nextPermissions = (draft.permissions || []).filter(
      permission => permission !== readPermission && permission !== managePermission
    );

    if (nextLevel === "view") {
      nextPermissions.push(readPermission);
    }
    if (nextLevel === "manage") {
      nextPermissions.push(managePermission);
    }

    handleInputChange("permissions", nextPermissions);
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px]",
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-1/2 top-1/2 z-50 flex h-fit max-h-[90vh] w-[calc(100%-2rem)] max-w-2xl -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden",
            "border border-border bg-background text-foreground shadow-2xl",
            "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]",
            "data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
            "rounded-xl",
            className
          )}
        >
          {/* Header */}
          <header className="flex flex-col space-y-1.5 border-b border-border bg-muted/20 p-6">
            <div className="flex items-center gap-2 mb-1">
              <div className="p-1.5 rounded-md bg-primary text-primary-foreground">
                <Shield className="w-4 h-4" />
              </div>
              <DialogPrimitive.Title className="text-xl font-bold tracking-tight leading-tight">
                {title}
              </DialogPrimitive.Title>
            </div>
            <DialogPrimitive.Description className="text-sm text-muted-foreground leading-relaxed">
              {description}
            </DialogPrimitive.Description>
          </header>

          {/* Form Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-8">
            {/* General Metadata */}
            <div className="space-y-4">
              <div className="grid gap-2">
                <Label htmlFor="displayName">Display Name</Label>
                <Input
                  id="displayName"
                  value={draft.displayName}
                  onChange={(e) => handleInputChange("displayName", e.target.value)}
                  placeholder="e.g. CI/CD Pipeline Automation"
                  className={cn(validation?.displayName && "border-red-500 focus-visible:ring-red-500")}
                />
                {validation?.displayName && (
                  <p className="text-[11px] font-medium text-red-600 dark:text-red-400">{validation.displayName}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
                    <Label htmlFor="expiresAt">Expiration Date</Label>
                  </div>
                  <Input
                    id="expiresAt"
                    type="date"
                    value={draft.expiresAt}
                    onChange={(e) => handleInputChange("expiresAt", e.target.value)}
                    className={cn(validation?.expiresAt && "border-red-500 focus-visible:ring-red-500")}
                  />
                  {validation?.expiresAt && (
                    <p className="text-[11px] font-medium text-red-600 dark:text-red-400">{validation.expiresAt}</p>
                  )}
                </div>
                <div className="grid gap-2">
                  <div className="flex items-center gap-2">
                    <Globe className="w-3.5 h-3.5 text-muted-foreground" />
                    <Label htmlFor="allowedIps">IP Allowlist (Optional)</Label>
                  </div>
                  <Input
                    id="allowedIps"
                    value={draft.allowedIps.join(", ")}
                    onChange={(e) => handleInputChange("allowedIps", e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
                    placeholder="e.g. 192.168.1.1, 10.0.0.0/24"
                    className={cn(validation?.allowedIps && "border-red-500 focus-visible:ring-red-500")}
                  />
                  {validation?.allowedIps && (
                    <p className="text-[11px] font-medium text-red-600 dark:text-red-400">{validation.allowedIps}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Permission Selection */}
            <div className="space-y-4 pt-4 border-t border-border">
              <div className="space-y-1">
                <Label className="text-sm font-bold">Permissions</Label>
                <p className="text-xs text-muted-foreground">
                  Choose the access level for each route family. Select the lowest level that still fits the integration.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-2">
                {permissionFamilies.map((family) => {
                  const hasManage = draft.permissions.includes(family.managePermission);
                  const hasView = hasManage || draft.permissions.includes(family.readPermission);

                  return (
                    <div
                      key={family.id}
                      className={cn(
                        "flex flex-col gap-3 p-3 rounded-lg border",
                        hasView
                          ? "border-primary bg-primary/[0.04] ring-1 ring-primary/20"
                          : "border-border bg-card",
                        family.disabled && "opacity-50 cursor-not-allowed grayscale"
                      )}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-foreground">{family.label}</span>
                            {hasManage ? (
                              <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-tight rounded-sm bg-primary/10 text-primary border border-primary/20">
                                Manage
                              </span>
                            ) : hasView ? (
                              <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-tight rounded-sm bg-muted text-muted-foreground border border-border">
                                View
                              </span>
                            ) : null}
                          </div>
                          {family.description && (
                            <p className="text-xs text-muted-foreground mt-1 leading-snug">
                              {family.description}
                            </p>
                          )}
                          {family.pathPrefixes && family.pathPrefixes.length > 0 ? (
                            <p className="text-[11px] text-muted-foreground/80 mt-2 leading-snug">
                              Routes: {family.pathPrefixes.join(", ")}
                            </p>
                          ) : null}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            type="button"
                            disabled={family.disabled}
                            onClick={() =>
                              setFamilyLevel(
                                family.readPermission,
                                family.managePermission,
                                hasManage || !hasView ? "view" : "none"
                              )
                            }
                            className={cn(
                              "px-3 py-1.5 text-[11px] font-bold rounded-md border transition-colors",
                              hasView && !hasManage
                                ? "border-primary bg-primary text-primary-foreground"
                                : "border-border bg-background text-foreground hover:bg-muted"
                            )}
                          >
                            View
                          </button>
                          <button
                            type="button"
                            disabled={family.disabled}
                            onClick={() =>
                              setFamilyLevel(
                                family.readPermission,
                                family.managePermission,
                                hasManage ? "none" : "manage"
                              )
                            }
                            className={cn(
                              "px-3 py-1.5 text-[11px] font-bold rounded-md border transition-colors",
                              hasManage
                                ? "border-primary bg-primary text-primary-foreground"
                                : "border-border bg-background text-foreground hover:bg-muted"
                            )}
                          >
                            Manage
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {validation?.permissions && (
                <p className="text-[11px] font-medium text-red-600 dark:text-red-400">{validation.permissions}</p>
              )}
            </div>
          </div>

          {/* Footer Actions */}
          <footer className="flex flex-col gap-4 border-t border-border bg-muted/20 p-6">
            {helperText && (
              <p className="text-xs text-muted-foreground text-center leading-relaxed italic">
                {helperText}
              </p>
            )}
            
            {validation?.general && (
              <div className="p-3 rounded-md bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/50 text-red-800 dark:text-red-400 text-xs font-medium text-center">
                {validation.general}
              </div>
            )}

            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
                className="px-4 py-2 text-sm font-bold rounded-md border border-border bg-background hover:bg-muted transition-colors disabled:opacity-50"
              >
                {cancelLabel}
              </button>
              <button
                type="button"
                onClick={onSubmit}
                disabled={submitting || draft.displayName.trim() === "" || draft.permissions.length === 0}
                className={cn(
                  "px-6 py-2 text-sm font-bold rounded-md bg-primary text-primary-foreground transition-colors",
                  "hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed",
                  submitting && "animate-pulse"
                )}
              >
                {submitting ? "Processing..." : submitLabel}
              </button>
            </div>
          </footer>

          {/* Close Button */}
          <DialogPrimitive.Close
            className={cn(
              "absolute right-4 top-4 rounded-md p-2 transition-all opacity-60",
              "hover:bg-accent hover:text-accent-foreground hover:opacity-100",
              "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            )}
          >
            <X className="h-5 w-5" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
